import { orgQueryKeys } from '@/config/queryKeys';
import { apiClient } from '@/lib/api-client';
import type { OrgCreate, OrgPublic, OrgUpdate } from '@/types';

/**
 * Creates a new organization using OpenAPI OrgCreate.
 * @param payload OrgCreate (OpenAPI)
 * @returns Promise resolving to OrgPublic
 */
export async function createOrganization(payload: OrgCreate): Promise<OrgPublic> {
  const result = await apiClient.post<OrgPublic>(orgQueryKeys.endpoints.list(), payload);
  if (!result) {
    throw new Error('Organization creation failed - no response from server');
  }
  return result;
}

/**
 * Fetches a list of all organizations.
 * GET /orgs → OrgPublic[]
 */
export async function listOrganizations(): Promise<OrgPublic[]> {
  const orgs = await apiClient.get<OrgPublic[]>(orgQueryKeys.endpoints.list());
  if (!orgs) {
    throw new Error('Failed to fetch organizations');
  }
  return orgs;
}

/**
 * Fetches a single organization by orgId.
 * GET /orgs/{org_id} → OrgPublic
 */
export async function getOrganization(orgId: string): Promise<OrgPublic> {
  if (!orgId) throw new Error('No organization ID provided');
  const org = await apiClient.get<OrgPublic>(orgQueryKeys.endpoints.detail(orgId));
  if (!org) throw new Error(`Failed to fetch org ${orgId}`);
  return org;
}

/**
 * Updates an existing organization.
 * PUT /orgs/{org_id} → OrgPublic
 */
export async function updateOrganization(orgId: string, payload: OrgUpdate): Promise<OrgPublic> {
  const result = await apiClient.put<OrgPublic>(orgQueryKeys.endpoints.detail(orgId), payload);
  if (!result) throw new Error('Organization update failed - no response from server');
  return result;
}
